import React, { useState, useEffect } from 'react';
import { Row, Col, Spin, Alert, Button, Card, Statistic, Progress } from 'antd';
import { ReloadOutlined, UserOutlined, ShoppingCartOutlined, CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import StatisticsApiService from '../../services/StatisticsApiService';

const StaffStatisticsDashboard = () => {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await StatisticsApiService.getStaffStatistics();
      setStatistics(response);
    } catch (err) {
      setError('Failed to load statistics. Please try again.');
      console.error('Error fetching statistics:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatistics();
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <p style={{ marginTop: '20px' }}>Loading statistics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        action={
          <Button size="small" danger onClick={fetchStatistics}>
            Retry
          </Button>
        }
      />
    );
  }

  // Helper function to safely get data from different possible structures
  const getData = (path) => {
    if (!statistics) return 0;
    
    // Try different possible data structures
    const paths = [
      path,
      `data.${path}`,
      `overview.${path}`,
      `data.overview.${path}`
    ];
    
    for (const dataPath of paths) {
      const value = dataPath.split('.').reduce((obj, key) => obj?.[key], statistics);
      if (value !== undefined && value !== null) {
        return value;
      }
    }
    
    return 0;
  };

  const totalLearners = getData('totalLearners');
  const totalOrders = getData('totalOrders');
  const completedOrders = getData('orderStatusBreakdown.completed');
  const pendingOrders = getData('orderStatusBreakdown.pending');
  const failedOrders = getData('orderStatusBreakdown.failed');
  const canceledOrders = getData('orderStatusBreakdown.canceled');

  return (
    <div className="statistics-dashboard">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px' 
      }}>
        <h2>Dashboard Management</h2>
        <Button 
          type="primary" 
          icon={<ReloadOutlined />} 
          onClick={fetchStatistics}
          loading={loading}
          className='add-btnw'
        >
          Refresh Data
        </Button>
      </div>

      {/* Overview Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card className="overview-stat-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <Statistic
                  title="Total Learners"
                  value={totalLearners}
                  valueStyle={{ 
                    color: '#1890ff', 
                    fontSize: '28px', 
                    fontWeight: 'bold' 
                  }}
                />
              </div>
              <div style={{ marginLeft: '16px' }}>
                <UserOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
              </div>
            </div>
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card className="overview-stat-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <Statistic
                  title="Total Orders"
                  value={totalOrders}
                  valueStyle={{ 
                    color: '#52c41a', 
                    fontSize: '28px', 
                    fontWeight: 'bold' 
                  }}
                />
              </div>
              <div style={{ marginLeft: '16px' }}>
                <ShoppingCartOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
              </div>
            </div>
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card className="overview-stat-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <Statistic
                  title="Completed Orders"
                  value={completedOrders}
                  valueStyle={{ 
                    color: '#fa8c16', 
                    fontSize: '28px', 
                    fontWeight: 'bold' 
                  }}
                />
              </div>
              <div style={{ marginLeft: '16px' }}>
                <CheckCircleOutlined style={{ fontSize: '24px', color: '#fa8c16' }} />
              </div>
            </div>
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card className="overview-stat-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <Statistic
                  title="Pending Orders"
                  value={pendingOrders}
                  valueStyle={{ 
                    color: '#f5222d', 
                    fontSize: '28px', 
                    fontWeight: 'bold' 
                  }}
                />
              </div>
              <div style={{ marginLeft: '16px' }}>
                <ClockCircleOutlined style={{ fontSize: '24px', color: '#f5222d' }} />
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Order Status Breakdown */}
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <Card title="Order Status Overview" className="chart-card">
            <div style={{ padding: '20px' }}>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '16px', 
                    backgroundColor: '#f6ffed', 
                    borderRadius: '8px',
                    border: '1px solid #b7eb8f'
                  }}>
                    <CheckCircleOutlined style={{ fontSize: '32px', color: '#52c41a', marginBottom: '8px' }} />
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                      {completedOrders}
                    </div>
                    <div style={{ fontSize: '14px', color: '#666' }}>Completed</div>
                    <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                      {totalOrders > 0 ? Math.round((completedOrders / totalOrders) * 100) : 0}%
                    </div>
                  </div>
                </Col>
                
                <Col span={12}>
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '16px', 
                    backgroundColor: '#fff7e6', 
                    borderRadius: '8px',
                    border: '1px solid #ffd591'
                  }}>
                    <ClockCircleOutlined style={{ fontSize: '32px', color: '#faad14', marginBottom: '8px' }} />
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#faad14' }}>
                      {pendingOrders}
                    </div>
                    <div style={{ fontSize: '14px', color: '#666' }}>Pending</div>
                    <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                      {totalOrders > 0 ? Math.round((pendingOrders / totalOrders) * 100) : 0}%
                    </div>
                  </div>
                </Col>
                
                <Col span={12}>
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '16px', 
                    backgroundColor: '#fff2f0', 
                    borderRadius: '8px',
                    border: '1px solid #ffccc7'
                  }}>
                    <CloseCircleOutlined style={{ fontSize: '32px', color: '#ff4d4f', marginBottom: '8px' }} />
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ff4d4f' }}>
                      {failedOrders}
                    </div>
                    <div style={{ fontSize: '14px', color: '#666' }}>Failed</div>
                    <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                      {totalOrders > 0 ? Math.round((failedOrders / totalOrders) * 100) : 0}%
                    </div>
                  </div>
                </Col>
                
                <Col span={12}>
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '16px', 
                    backgroundColor: '#fafafa', 
                    borderRadius: '8px',
                    border: '1px solid #d9d9d9'
                  }}>
                    <CloseCircleOutlined style={{ fontSize: '32px', color: '#d9d9d9', marginBottom: '8px' }} />
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#d9d9d9' }}>
                      {canceledOrders}
                    </div>
                    <div style={{ fontSize: '14px', color: '#666' }}>Canceled</div>
                    <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                      {totalOrders > 0 ? Math.round((canceledOrders / totalOrders) * 100) : 0}%
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </Card>
        </Col>
        
        <Col xs={24} lg={12}>
          <Card title="Most Purchased Courses" className="chart-card">
            <div style={{ padding: '20px', textAlign: 'center' }}>
              {statistics?.data?.mostpurchasedCourses?.length > 0 || statistics?.mostpurchasedCourses?.length > 0 ? (
                <div>
                  {/* Course list would go here */}
                  <p>Course purchase data available</p>
                </div>
              ) : (
                <div style={{ color: '#999', padding: '40px 0' }}>
                  <ShoppingCartOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                  <p>No course purchase data available yet</p>
                  <p style={{ fontSize: '12px' }}>Purchase data will appear here as students buy courses</p>
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default StaffStatisticsDashboard; 